import { useState } from 'react';
import { useDemoMutation } from '@/features/demo/hooks';
import { ModalWrapper } from '@/shared';

interface DemoRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DemoRequestModal = ({ isOpen, onClose }: DemoRequestModalProps) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organizationName: '',
    numberOfFacilities: null as number | null,
    numberOfResidents: null as number | null,
    currentSoftware: '',
    message: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const demoMutation = useDemoMutation();

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim() || formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      newErrors.email = 'Please provide a valid email address';
    }

    const phoneRegex =
      /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
    if (!formData.phone.trim() || !phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Please provide a valid phone number';
    }

    if (
      !formData.organizationName.trim() ||
      formData.organizationName.length < 2
    ) {
      newErrors.organizationName =
        'Organization name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name.includes('number')
        ? value
          ? parseInt(value)
          : null
        : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    demoMutation.mutate(
      {
        ...formData,
        source: 'hero-section',
      },
      {
        onSuccess: () => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            organizationName: '',
            numberOfFacilities: null,
            numberOfResidents: null,
            currentSoftware: '',
            message: '',
          });
          setErrors({});
          onClose();
        },
      },
    );
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-center min-h-screen p-5">
        <div className="bg-white rounded-4xl p-8 max-w-2xl w-full">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 py-4 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-[#5761EB]">Book a Demo</h2>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#5761EB] ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="John Smith"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#5761EB] ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="john@example.com"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#5761EB] ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="+1 (555) 123-4567"
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}
              </div>

              {/* Organization Name */}
              <div>
                <label
                  htmlFor="organizationName"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Organization Name *
                </label>
                <input
                  type="text"
                  id="organizationName"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#5761EB] ${
                    errors.organizationName
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                  placeholder="Acme Sober Living"
                />
                {errors.organizationName && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.organizationName}
                  </p>
                )}
              </div>

              {/* Number of Facilities */}
              <div>
                <label
                  htmlFor="numberOfFacilities"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Number of Facilities
                </label>
                <select
                  id="numberOfFacilities"
                  name="numberOfFacilities"
                  value={formData.numberOfFacilities || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#5761EB]"
                >
                  <option value="">Select...</option>
                  <option value="1">1</option>
                  <option value="2">2-5</option>
                  <option value="6">6-10</option>
                  <option value="11">10+</option>
                </select>
              </div>

              {/* Number of Residents */}
              <div>
                <label
                  htmlFor="numberOfResidents"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Number of Residents
                </label>
                <select
                  id="numberOfResidents"
                  name="numberOfResidents"
                  value={formData.numberOfResidents || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#5761EB]"
                >
                  <option value="">Select...</option>
                  <option value="1">1-25</option>
                  <option value="26">26-50</option>
                  <option value="51">51-100</option>
                  <option value="101">100+</option>
                </select>
              </div>
            </div>

            {/* Current Software */}
            <div className="mt-6">
              <label
                htmlFor="currentSoftware"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Current Software (if any)
              </label>
              <input
                type="text"
                id="currentSoftware"
                name="currentSoftware"
                value={formData.currentSoftware}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#5761EB]"
                placeholder="e.g., Excel, Google Sheets, Custom software"
              />
            </div>

            {/* Message */}
            <div className="mt-6">
              <label
                htmlFor="message"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Additional Notes
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#5761EB]"
                placeholder="Tell us about your specific needs or questions..."
              />
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={demoMutation.isPending}
                className={`flex-1 px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
                  demoMutation.isPending
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#5761EB] hover:bg-[#4651DB]'
                }`}
              >
                {demoMutation.isPending ? 'Submitting...' : 'Request Demo'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default DemoRequestModal;
